import { useState, type MouseEvent } from 'react';
import { Btn } from '../primitives/Btn';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { SystemArchitectureSVG } from '../primitives/SystemArchitectureSVG';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  ACCENT,
  BORDER,
  EASE,
  EASE_SPRING,
  FONT_DISPLAY,
  FONT_MONO,
  MUTED,
  SURFACE2,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../tokens';

interface ProcessTab {
  num: string;
  title: string;
  cost: string;
  desc: string;
}

const TABS: readonly ProcessTab[] = [
  {
    num: '01',
    title: 'Momentum Engine',
    cost: 'Entry',
    desc: 'Two agents on the two places you lose the most. The Speed-to-Lead Agent fires the moment someone submits through your ads or a web form, holds a real conversation, qualifies them and gets a booking link in front of them before the intent decays. The Reputation Agent fires when a job is marked complete and asks for the review on whatever channel that customer actually uses.',
  },
  {
    num: '02',
    title: 'Content OS',
    cost: 'Start Here',
    desc: 'The full system. I scan your competitors for what is landing, mine your own calls and inboxes for the objections that keep coming up, then write, schedule and publish against both across LinkedIn, Instagram and Facebook. Meta ads run alongside it, cut from the content already proven to land, and a weekly report tells you what actually worked and why.',
  },
  {
    num: '03',
    title: 'Performance Layer',
    cost: 'Later',
    desc: 'Once the front end is booking and building trust on its own, this is where the rest of the operation gets the same treatment. Technical execution, data reporting and objection mining moved inside your own tools. This is the two-hour week applied to the whole business, and it is a later conversation, not a first one.',
  },
];

const ACTIVE_TAB_SHADOW = '0 0 18px rgba(255,107,0,0.25), 0 0 40px rgba(255,107,0,0.1)';

interface ProcessSectionProps {
  ctaHref: string;
  onCta: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export function ProcessSection({ ctaHref, onCta }: ProcessSectionProps) {
  const [activeTab, setActiveTab] = useState(0);
  const isMobile = useIsMobile();

  const tab = TABS[activeTab] ?? TABS[0];
  if (!tab) return null;

  return (
    <section
      id="process"
      style={{ padding: isMobile ? '40px 20px' : '130px 64px', background: 'rgba(8,8,8,0.88)' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ marginBottom: isMobile ? 24 : 52 }}>
            <SectionLabel number="03">How It Works</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>
              Start where it hurts most. Scale when it has already paid for itself.
            </SectionH2>
            <p style={{ color: TEXT_SECONDARY, fontSize: '1.05rem', lineHeight: 1.75, maxWidth: 580 }}>
              Three tiers, and most people do not start at the top. I show you the gap first with a free
              competitor scan, then you pick the tier that matches what is actually costing you, and move
              up only once the one below it is working.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={80}>
          {/* Tab headers */}
          <div
            role="tablist"
            aria-label="Engagement stages"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: isMobile ? 4 : 8,
              marginBottom: isMobile ? 16 : 40,
              borderBottom: `1px solid ${BORDER}`,
              padding: isMobile ? '12px 0 8px' : '36px 16px 28px',
              flexWrap: 'wrap',
            }}
          >
            {TABS.map((t, idx) => {
              const isActive = activeTab === idx;

              return (
                <button
                  key={t.num}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(idx)}
                  style={{
                    background: isActive ? 'rgba(255,107,0,0.1)' : 'transparent',
                    border: `1px solid ${isActive ? 'rgba(255,107,0,0.5)' : 'transparent'}`,
                    borderRadius: 12,
                    padding: isMobile ? '8px 12px' : '12px 32px',
                    fontFamily: FONT_DISPLAY,
                    fontSize: isMobile ? '0.75rem' : '1rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: isActive ? ACCENT : MUTED,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: `all 0.3s ${EASE}`,
                    boxShadow: isActive ? ACTIVE_TAB_SHADOW : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.borderColor = 'rgba(255,107,0,0.3)';
                    e.currentTarget.style.background = 'rgba(255,107,0,0.05)';
                  }}
                  onMouseLeave={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {t.num} {isMobile ? t.title.split(' ')[0] : t.title}
                  {isActive && (
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        bottom: -9,
                        left: 0,
                        right: 0,
                        height: 2,
                        background: ACCENT,
                        animation: `scaleIn 0.3s ${EASE_SPRING}`,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Keying on activeTab remounts the panel, replaying the fade-in. */}
          <div
            key={activeTab}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 16 : 60,
              alignItems: 'center',
              animation: `tabFadeIn 0.35s ${EASE}`,
            }}
          >
            <div style={{ padding: isMobile ? 0 : '20px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: isMobile ? 10 : 20 }}>
                <span style={{ fontFamily: FONT_MONO, fontSize: '0.7rem', color: ACCENT, letterSpacing: '0.1em' }}>
                  {tab.num}
                </span>
                <span
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: '0.65rem',
                    background: 'rgba(255,107,0,0.12)',
                    color: ACCENT,
                    padding: '5px 14px',
                    borderRadius: 100,
                  }}
                >
                  {tab.cost.toUpperCase()}
                </span>
              </div>

              <h3
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: isMobile ? '1.6rem' : 'clamp(2rem,3vw,2.5rem)',
                  fontWeight: 400,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  marginBottom: isMobile ? 8 : 14,
                  color: TEXT_PRIMARY,
                }}
              >
                {tab.title}
              </h3>

              <p
                style={{
                  color: TEXT_SECONDARY,
                  fontSize: isMobile ? '0.9rem' : '1.05rem',
                  lineHeight: isMobile ? 1.55 : 1.75,
                }}
              >
                {tab.desc}
              </p>

              <div style={{ marginTop: isMobile ? 16 : 24 }}>
                <Btn href={ctaHref} onClick={onCta}>Get My Free Competitor Scan →</Btn>
              </div>
            </div>

            <div
              style={{
                background: SURFACE2,
                borderRadius: 20,
                padding: isMobile ? 12 : 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div style={{ width: '100%', maxWidth: isMobile ? 240 : 440 }}>
                <SystemArchitectureSVG activeTab={activeTab} />
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
